import { useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  HeaderLogo,
  FooterDisclaimer,
  FooterNav,
} from 'organisms';
import { DeviceTypes, useDeviceType } from 'common';
import {
  Button,
  Headings,
  Paragraph,
  MaintenanceIcon,
  Link,
} from '@sentara-health/optimahealth_web';
import { GlobalContext, isAvMed } from 'context';
import { get } from 'http';

// Separate header component
const Header = () => (
  <header className="border-bottom">
    <div className="container-fluid">
      <nav className="header container-xl d-flex align-items-center justify-content-between">
        <HeaderLogo />
      </nav>
    </div>
  </header>
);

// Separate footer component
const Footer = ({ isMobile }: { isMobile: boolean }) => (
  <>
    {!isAvMed() && isMobile ? <hr className="mb-4 mx-3" /> : null}
    <footer className={`${isAvMed() ? '' : 'mt-4'}`}>
      {isAvMed() && <FooterNav />}
      <div className="container-xl">
        <FooterDisclaimer />
      </div>
    </footer>
  </>
);

// Maintenance content component
const MaintenanceContent = ({
  isMobile,
  t,
  getFeature,
  ssoEndPoints
}: {
  isMobile: boolean;
  t: (key: string) => string;
  getFeature: any;
  ssoEndPoints: any;
}) => {
  const TitleComponent = isMobile ? Paragraph : Headings;

  return (
    <div className="col-12 px-0">
      <TitleComponent
        level={isMobile ? 4 : 5}
        text={t('maintenance.maintenanceTitle')}
        variant={`${isAvMed() ? "semiBold" : "bold"}`}
        className={`mb-sm-3 mb-3 ${isAvMed() && !getFeature?.FormerAvmedMembers && isMobile ? "mt-n2" : ""}`}
      />
      <MaintenanceMessage t={t} getFeature={getFeature} />
      {getFeature?.FormerAvmedMembers && (
        <FormerMemberSection
          isMobile={isMobile}
          t={t}
          ssoEndPoints={ssoEndPoints}
        />
      )}
    </div>
  );
};

// Maintenance message component
const MaintenanceMessage = ({ t, getFeature }: { t: (key: string) => string, getFeature: any }) => (
  <Paragraph
    level={2}
    text={
      <Trans
        i18nKey="maintenance.maintenancePage"
        values={{ phone: t("maintenance.maintenanceNo") }}
        components={{
          link1: <Link to={`tel:+${t("maintenance.maintenanceNo")}`} data-testid="paymentPhoneLink" className="maintenance-phone-link" />,
          link2: (
            <Link
              to={t("common.memberResourcesURL")}
              target="_blank"
              className="text-decoration-underline maintenance-link"
              dataTestId="paymentOptionsLink"
            >
              {t("maintenance.memberResourcesPage")}
            </Link>
          ),
        }}
      />
    }
    variant="regular"
    className={`${isAvMed() && getFeature?.FormerAvmedMembers ? "mb-sm-4" : "mb-sm-0"} mb-3`}
  />
);

// Former member section component
const FormerMemberSection = ({
  isMobile,
  t,
  ssoEndPoints
}: {
  isMobile: boolean;
  t: (key: string) => string;
  ssoEndPoints: any;
}) => {
  const TitleComponent = isMobile ? Paragraph : Headings;

  return (
    <div className="col-12 px-0 maintenance-border">
      <TitleComponent
        level={isMobile ? 4 : 5}
        text={t('maintenance.title')}
        variant="semiBold"
        className={isMobile ? "mb-2" : ""}
      />
      <Paragraph
        level={2}
        text={
          <Trans
            i18nKey="maintenance.description"
            components={{ strong: <strong /> }}
          />
        }
        variant="regular"
        className="mb-sm-4 mb-3"
      />
      <Button
        dataTestId="AccessFormerMemberButton"
        type="button"
        onClick={() => window.open(ssoEndPoints?.formerAvmedMember, '_blank')}
        variant="secondary_btn"
        className="bg-transparent"
      >
        {t('common.buttonText')}
      </Button>
    </div>
  );
};

const MaintenancePage = () => {
  const { getFeature, ssoEndPoints } = useContext(GlobalContext);
  const { t } = useTranslation();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const isTablet = deviceType === DeviceTypes.TABLET;

  const getContFluidClass = () => {
    if (isAvMed() && (isTablet || isMobile)) {
      return 'avmed-bg px-3';
    } else if (isTablet) {
      return 'shp-bg py-sm-5';
    } else if (isMobile) {
      return 'p-2 pt-4';
    }
    return '';
  };
  const getBg = () => isAvMed() ? 'avmed-bg' : 'shp-bg py-sm-5';
  const getFirstRowClass = () => {
    if (isAvMed()) {
      if (getFeature?.FormerAvmedMembers || isMobile) {
        return 'col-12';
      }
      return "align-items-start py-sm-5";
    }
    return isMobile ? "shp-bg col-12 px-3" : "py-sm-5 my-sm-5 col-12";
  }
  const getSecondRowClass = () => {
    if (isAvMed()) {
      if (getFeature?.FormerAvmedMembers) {
        return 'align-items-start py-lg-5 my-md-5';
      }
      return 'align-items-center py-xl-6 py-sm-5 my-md-4';
    }
    return 'align-items-center py-4';
  }

  const getMaintenanceIconClasses = () => {
    if (isAvMed()) {
      if (getFeature?.FormerAvmedMembers) {
        return 'former-avmed-maintenance-icon';
      }
      return isMobile ? 'maintenance-icon pt-3' : 'maintenance-icon';
    }
    return 'maintenance-icon';
  }
  const getMaintenanceContent = () => isAvMed() ? "py-3" : "";

  return (
    <div className="d-flex flex-column h-100">
      <Header />
      <div className={`container-fluid p-0 ${getContFluidClass()}`}>
        <div className={`container-xl px-sm-3 px-2 ${(isTablet || isMobile) ? '' : getBg()}`}>
          <div className={`d-flex flex-column flex-md-row justify-content-center ${getFirstRowClass()}`}>
            <div className={`d-flex flex-column flex-md-row justify-content-center ${getSecondRowClass()}`}>
              <div className={`col-12 col-sm-auto me-xl-0 me-lg-2 ${getMaintenanceIconClasses()}`}>
                <MaintenanceIcon />
              </div>
              <div className={`col-xl-5 col-lg-6 col-md-8 col-sm-9 col-12 ps-xl-5 ps-sm-4 ${getMaintenanceContent()}`}>
                <MaintenanceContent
                  isMobile={isMobile}
                  t={t}
                  getFeature={getFeature}
                  ssoEndPoints={ssoEndPoints}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer isMobile={isMobile} />
    </div>
  );
};

export default MaintenancePage;
